import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  X,
  Link2,
  Star,
  CalendarDays,
  ChevronDown,
  AlertTriangle,
  GraduationCap,
  Clock,
  MapPin,
  BarChart3,
  Users,
  CheckCircle2,
  CalendarPlus,
  Lightbulb,
  Sparkles,
  FileCheck2,
  Award,
  Wallet,
  FileText,
} from 'lucide-react';
import { getProTraining, activeerProTraining, downloadProAgenda } from '../../services/professionalAcademyApi';
import type { ProSessie, ProTraining, ProTrainingDetail, ProWallet } from '../../services/professionalAcademyApi';
import { LEERVORM, WKR_ORANJE, accentVoor, datumLang, eur, pePunten, prijs, tijd } from './proAcademyStijl';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const Tekstblok: React.FC<{ titel: string; tekst: string | null | undefined }> = ({ titel, tekst }) =>
  tekst?.trim() ? (
    <section>
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{titel}</h3>
      {/* Opleider-invoer is platte tekst: nooit als HTML renderen. */}
      <p className="mt-1.5 whitespace-pre-line text-[15px] leading-relaxed text-gray-700">{tekst.trim()}</p>
    </section>
  ) : null;

const ReviewBalk: React.FC<{ label: string; waarde: number }> = ({ label, waarde }) => (
  <div>
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-gray-600">{label}</span>
      <span className="inline-flex items-center gap-1 font-semibold text-gray-900">
        <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
        {waarde.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
      </span>
    </div>
    <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuemin={1} aria-valuemax={5} aria-valuenow={waarde} aria-label={label}>
      <div className="h-full rounded-full bg-amber-400" style={{ width: `${(waarde / 5) * 100}%` }} />
    </div>
  </div>
);

/** "Tijdlijn-openklapper": alleen de startdatum is klikbaar; vervolgdata klappen uit als read-only tijdlijn. */
const SessieSlot: React.FC<{ sessie: ProSessie; gekozen: boolean; onKies: () => void }> = ({ sessie, gekozen, onKies }) => {
  const { t } = useTranslation('pages');
  const [open, setOpen] = useState(false);
  const boekbaar = sessie.status === 'AVAILABLE' && sessie.vrij > 0;
  const naam = sessie.naam || t('proAcademy.detail.sessie', { defaultValue: 'Sessie' });
  const n = sessie.lessen.length;

  return (
    <li
      className={`rounded-xl border p-3 transition ${
        !boekbaar ? 'border-red-200 bg-red-50' : gekozen ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      <label className={`flex items-start gap-2.5 ${boekbaar ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
        <input type="radio" name="pro-academy-sessie" checked={gekozen} disabled={!boekbaar} onChange={onKies} className="mt-0.5 h-4 w-4 accent-emerald-500" />
        <span className="min-w-0 flex-1 text-sm">
          <span className={`font-semibold ${boekbaar ? 'text-gray-900' : 'text-gray-500'}`}>{naam}:</span>{' '}
          <span className={boekbaar ? 'text-gray-700' : 'text-gray-500'}>
            {sessie.start_at ? t('proAcademy.detail.startOp', { defaultValue: 'Start op {{datum}}', datum: datumLang(sessie.start_at) }) : '—'}
          </span>
          <span className="mt-0.5 block text-xs">
            {sessie.status === 'EXPIRED' ? (
              <span className="font-medium text-red-700">❌ {t('proAcademy.detail.verlopen', { defaultValue: 'Verlopen' })}</span>
            ) : !boekbaar ? (
              <span className="font-medium text-red-700">🔴 {t('proAcademy.detail.vol', { defaultValue: 'VOL' })}</span>
            ) : (
              <span className={sessie.vrij <= 5 ? 'font-medium text-amber-700' : 'text-emerald-700'}>
                {sessie.vrij === 1
                  ? t('proAcademy.detail.nogEenVrij', { defaultValue: 'Nog 1 plek vrij' })
                  : t('proAcademy.detail.nogVrij', { defaultValue: 'Nog {{n}} plekken vrij', n: sessie.vrij })}
              </span>
            )}
          </span>
        </span>
      </label>

      {n > 1 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="ml-6 mt-2 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
          >
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            {t('proAcademy.detail.toonLesdata', { defaultValue: 'Toon alle {{n}} lesdata', n })}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
            <div className="overflow-hidden">
              <ol className="ml-7 border-l border-gray-200" aria-label={t('proAcademy.detail.lesschema', { defaultValue: 'Lesschema' })}>
                {sessie.lessen.map((les) => (
                  <li key={les.volgnummer} className="relative py-1.5 pl-4 text-xs text-gray-600 first:mt-2 last:mb-1">
                    <span className={`absolute -left-[5px] top-2.5 h-2.5 w-2.5 rounded-full ${les.volgnummer === 1 ? 'bg-emerald-500' : 'bg-gray-300'}`} aria-hidden="true" />
                    <span className="font-medium text-gray-900">
                      {les.volgnummer === 1
                        ? t('proAcademy.detail.lesStart', { defaultValue: 'Les 1 (Start)' })
                        : t('proAcademy.detail.lesN', { defaultValue: 'Les {{n}}', n: les.volgnummer })}
                      :
                    </span>{' '}
                    📅 {datumLang(les.start_at)} | {tijd(les.start_at)} - {tijd(les.eind_at)} {t('proAcademy.detail.uur', { defaultValue: 'uur' })}
                    {les.locatie && <span className="block text-gray-500">{les.locatie}</span>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </>
      )}
    </li>
  );
};

/**
 * Informatie-scherm (kamerbrede detail-modal): links (70%) cover, accreditatie-details,
 * leerdoelen en reviewbalken; rechts (30%) de transparante financiële breakdown met het
 * fictieve PE-budget, de startmoment-slots en [ 🎓 Activeer Cursus & Registreer PE-punten ].
 */
export const ProAcademyDetailModal: React.FC<{
  training: ProTraining;
  wallet: ProWallet | undefined;
  onClose: () => void;
}> = ({ training: basis, wallet, onClose }) => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const sluitKnop = useRef<HTMLButtonElement>(null);
  const [sessieId, setSessieId] = useState<number | null>(null);
  const [geactiveerd, setGeactiveerd] = useState<{ registratieId: number; metAgenda: boolean; pe: number | null } | null>(null);

  const detailQuery = useQuery({ queryKey: ['pro-academy-training', basis.id], queryFn: () => getProTraining(basis.id) });
  const detail: ProTrainingDetail | undefined = detailQuery.data;
  const tr: ProTraining = detail ?? basis;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    sluitKnop.current?.focus();
    const vorig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const toets = (e: KeyboardEvent) => e.key === 'Escape' && onCloseRef.current();
    window.addEventListener('keydown', toets);
    return () => {
      document.body.style.overflow = vorig;
      window.removeEventListener('keydown', toets);
    };
  }, []);

  const activeerMutation = useMutation({
    mutationFn: () => activeerProTraining(tr.id, sessieId),
    onSuccess: (res) => {
      setGeactiveerd({ registratieId: res.registratie_id, metAgenda: Boolean(res.sessie_id), pe: res.pe_punten });
      queryClient.invalidateQueries({ queryKey: ['pro-academy'] });
      queryClient.invalidateQueries({ queryKey: ['pro-academy-training', tr.id] });
    },
    onError: () => queryClient.invalidateQueries({ queryKey: ['pro-academy-training', tr.id] }),
  });
  const agendaMutation = useMutation({ mutationFn: (id: number) => downloadProAgenda(id, tr.titel) });

  const accent = accentVoor(tr);
  const vorm = LEERVORM[tr.leervorm];
  const VormIcon = vorm.icon;
  const sessies = detail?.sessies ?? [];
  const heeftSessies = tr.beschikbaarheid.heeft_sessies;
  const boekbareSessies = sessies.filter((s) => s.status === 'AVAILABLE' && s.vrij > 0);
  const sessieVerplicht = heeftSessies && sessieId === null;
  const budgetTekort = Boolean(wallet) && tr.netto_bedrag > (wallet?.budget_resterend ?? 0);
  const kanActiveren = !sessieVerplicht && !(heeftSessies && boekbareSessies.length === 0) && !budgetTekort && Boolean(detail);

  const knopTekst =
    heeftSessies && boekbareSessies.length === 0
      ? t('proAcademy.detail.geenDatum', { defaultValue: 'Wacht op nieuwe datum' })
      : sessieVerplicht
        ? t('proAcademy.detail.kiesDatum', { defaultValue: 'Kies eerst een startdatum' })
        : budgetTekort
          ? t('proAcademy.detail.budgetTekort', { defaultValue: 'PE-budget ontoereikend' })
          : tr.accreditatie_badge
            ? t('proAcademy.detail.activeerPe', { defaultValue: '🎓 Activeer Cursus & Registreer PE-punten' })
            : t('proAcademy.detail.activeer', { defaultValue: '🎓 Activeer Cursus' });

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-slate-900/40 backdrop-blur-sm" onClick={() => !activeerMutation.isPending && onClose()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pro-academy-detail-titel"
        className="relative mx-auto my-0 min-h-full w-full max-w-6xl overflow-hidden bg-gray-50 text-gray-700 shadow-2xl ring-1 ring-gray-200 sm:my-8 sm:min-h-0 sm:rounded-2xl"
        style={{ boxShadow: `0 0 0 1px ${accent}40, 0 25px 50px -12px rgba(10,25,47,.35)` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cover */}
        <div className="relative aspect-video max-h-[26rem] w-full overflow-hidden sm:aspect-[21/8]" style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}>
          {tr.omslagfoto_url ? (
            <img src={tr.omslagfoto_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-8xl"
              style={{ background: `radial-gradient(120% 90% at 30% 20%, ${accent}55 0%, #0b1020 60%, #05070c 100%)` }}
              aria-hidden="true"
            >
              {tr.subcategorie_emoji || tr.hoofdmap_emoji}
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/90 via-[#0A192F]/30 to-transparent" aria-hidden="true" />
          <button
            ref={sluitKnop}
            type="button"
            onClick={onClose}
            aria-label={t('proAcademy.sluiten', { defaultValue: 'Sluiten' })}
            className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-white backdrop-blur hover:bg-black"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
          <div className="absolute inset-x-0 bottom-0 px-5 pb-4 sm:px-8">
            <div className="flex flex-wrap items-center gap-2">
              {tr.accreditatie_badge && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-gray-900 shadow-lg">
                  <GraduationCap className="h-4 w-4 text-[#0B1F3A]" aria-hidden="true" />
                  {tr.accreditatie_badge.label}
                </span>
              )}
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white ${vorm.badge}`}>
                <VormIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {t(`proAcademy.leervormLabel.${tr.leervorm}`, { defaultValue: vorm.label })}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white" style={{ backgroundColor: `${accent}cc` }}>
                {tr.hoofdmap_emoji} {tr.hoofdmap_label}
              </span>
              {tr.rating && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-bold text-white">
                  <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
                  {tr.rating.gemiddelde.toLocaleString('nl-NL', { minimumFractionDigits: 1 })}
                  <span className="font-normal text-slate-400">({tr.rating.aantal})</span>
                </span>
              )}
              {tr.populair && (
                <span className="inline-flex items-center gap-1 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-orange-300">
                  🔥 {t('proAcademy.tegel.populair', { defaultValue: 'Populair' })}
                </span>
              )}
            </div>
            <h2 id="pro-academy-detail-titel" className="mt-2 text-2xl font-extrabold leading-tight text-white sm:text-4xl">
              {tr.titel}
            </h2>
          </div>
        </div>

        <div className="grid gap-8 px-5 pb-8 pt-4 sm:px-8 lg:grid-cols-[7fr_3fr]">
          {/* Linkerkant (70%) */}
          <div className="min-w-0 space-y-6">
            {tr.pitch && (
              <p className="flex items-start gap-2 text-lg font-semibold leading-snug text-gray-900">
                <Lightbulb className="mt-1 h-5 w-5 shrink-0 text-amber-700" aria-hidden="true" />
                {tr.pitch}
              </p>
            )}

            {detailQuery.isLoading && <p className="text-sm text-gray-500">…</p>}
            {detailQuery.isError && (
              <p className="text-sm text-red-700" role="alert">
                {foutMelding(detailQuery.error, t('proAcademy.detail.laadfout', { defaultValue: 'De details konden niet worden geladen.' }))}
              </p>
            )}

            {/* Accreditatie-details */}
            {(tr.accreditatie_badge || tr.passie || detail?.accreditatie) && (
              <section className="rounded-xl border p-4" style={{ borderColor: tr.passie ? `${WKR_ORANJE}66` : '#e5e7eb', backgroundColor: tr.passie ? `${WKR_ORANJE}0d` : '#ffffff' }}>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {t('proAcademy.detail.accreditatie', { defaultValue: 'Accreditatie & PE' })}
                </h3>
                {tr.accreditatie_badge ? (
                  <p className="mt-2 flex items-start gap-2 text-[15px] text-gray-900">
                    <GraduationCap className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" aria-hidden="true" />
                    <span>
                      <strong>{pePunten(tr.accreditatie_badge.punten)} PE-punten</strong>
                      {tr.accreditatie_badge.register && (
                        <>
                          {' '}
                          {t('proAcademy.detail.geldigVoor', { defaultValue: 'geldig voor register' })} <strong>{tr.accreditatie_badge.register}</strong>
                        </>
                      )}
                      <span className="block text-sm text-gray-500">
                        {t('proAcademy.detail.peUitleg', { defaultValue: 'De punten worden bij activering geregistreerd in uw PE-overzicht van {{jaar}}.', jaar: wallet?.boekjaar ?? new Date().getFullYear() })}
                      </span>
                    </span>
                  </p>
                ) : tr.passie ? (
                  <p className="mt-2 flex items-start gap-2 text-sm" style={{ color: '#B45309' }}>
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    {t('proAcademy.detail.passieUitleg', {
                      defaultValue: 'Persoonlijke Passie & Hobby: hobbymatige ontspanning buiten uw vakgebied. Deze cursus levert géén PE-accreditatiepunten op.',
                    })}
                  </p>
                ) : null}
                {detail?.accreditatie && <p className="mt-2 whitespace-pre-line text-sm text-gray-600">{detail.accreditatie}</p>}
              </section>
            )}

            <Tekstblok titel={t('proAcademy.detail.omschrijving', { defaultValue: 'Omschrijving' })} tekst={tr.samenvatting} />
            <Tekstblok titel={t('proAcademy.detail.leerdoelen', { defaultValue: 'Leerdoelen' })} tekst={detail?.leerdoelen} />
            <Tekstblok titel={t('proAcademy.detail.programma', { defaultValue: 'Programma' })} tekst={detail?.programma_onderdelen} />
            <Tekstblok titel={t('proAcademy.detail.voorWie', { defaultValue: 'Voor wie' })} tekst={detail?.voor_wie || detail?.doelgroep} />
            <Tekstblok titel={t('proAcademy.detail.toelatingseisen', { defaultValue: 'Toelatingseisen' })} tekst={detail?.toelatingseisen} />

            {detail && (
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{t('proAcademy.detail.reviews', { defaultValue: 'Beoordelingen' })}</h3>
                {detail.reviews.balken ? (
                  <div className="mt-2 grid gap-3 sm:grid-cols-3">
                    <ReviewBalk label={t('proAcademy.detail.relevantie', { defaultValue: 'Relevantie voor uw praktijk' })} waarde={detail.reviews.balken.relevantie} />
                    <ReviewBalk label={t('proAcademy.detail.toepasbaarheid', { defaultValue: 'Direct toepasbaar' })} waarde={detail.reviews.balken.toepasbaarheid} />
                    <ReviewBalk label={t('proAcademy.detail.expertise', { defaultValue: 'Expertise docent' })} waarde={detail.reviews.balken.expertise} />
                  </div>
                ) : (
                  <p className="mt-1.5 text-sm text-gray-500">
                    {t('proAcademy.detail.nogGeenReviews', { defaultValue: 'Scores worden zichtbaar vanaf 5 beoordelingen ({{n}} tot nu toe).', n: detail.reviews.aantal })}
                  </p>
                )}
              </section>
            )}

            {/* Metadata-kolom (Laag B: erkende aanbieder als groen oplichtende link) */}
            <dl className="grid gap-x-6 gap-y-3 rounded-xl border border-gray-200 bg-white p-4 text-sm sm:grid-cols-2">
              <div className="sm:col-span-2">
                <dt className="text-gray-500">{t('proAcademy.detail.aanbieder', { defaultValue: 'Erkende aanbieder' })}</dt>
                <dd>
                  <Link
                    to={`/opleidingen/opleider/${tr.provider_id}`}
                    onClick={onClose}
                    className="inline-flex items-center gap-1.5 font-semibold text-emerald-700 underline decoration-emerald-600/40 underline-offset-2 hover:text-emerald-800 "
                  >
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                    {tr.organisatie_naam}
                    <span className="font-normal text-emerald-700/80">({t('proAcademy.detail.bekijkProfiel', { defaultValue: 'Bekijk profiel' })})</span>
                  </Link>
                </dd>
              </div>
              {tr.duur && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.duur', { defaultValue: 'Duur' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <Clock className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {tr.duur}
                  </dd>
                </div>
              )}
              {detail?.niveau && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.niveau', { defaultValue: 'Niveau' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <BarChart3 className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {detail.niveau}
                  </dd>
                </div>
              )}
              {(tr.locatie_detail || tr.locaties) && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.locatie', { defaultValue: 'Locatie' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {tr.locatie_detail || tr.locaties}
                  </dd>
                </div>
              )}
              {detail?.studiebelasting && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.studiebelasting', { defaultValue: 'Studiebelasting' })}</dt>
                  <dd className="text-gray-900">{detail.studiebelasting}</dd>
                </div>
              )}
              {tr.certificering && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.certificering', { defaultValue: 'Certificering' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <Award className="h-4 w-4 text-emerald-700" aria-hidden="true" />
                    {tr.certificering}
                  </dd>
                </div>
              )}
              {tr.inclusief_examen && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.examen', { defaultValue: 'Examen' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <FileCheck2 className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {t('proAcademy.tegel.examen', { defaultValue: 'Inclusief examen' })}
                  </dd>
                </div>
              )}
              {detail?.docenten && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.docenten', { defaultValue: 'Docenten' })}</dt>
                  <dd className="inline-flex items-center gap-1.5 text-gray-900">
                    <Users className="h-4 w-4 text-gray-500" aria-hidden="true" />
                    {detail.docenten}
                  </dd>
                </div>
              )}
              {detail?.brochure_url && (
                <div>
                  <dt className="text-gray-500">{t('proAcademy.detail.brochure', { defaultValue: 'Brochure' })}</dt>
                  <dd>
                    <a href={detail.brochure_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sky-700 hover:text-sky-900">
                      <FileText className="h-4 w-4" aria-hidden="true" />
                      {t('proAcademy.detail.brochureDownload', { defaultValue: 'Download (PDF)' })}
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Rechterkant (30%): transparante financiële breakdown + fictief budget + inkoop */}
          <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
            {geactiveerd ? (
              <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-5 text-center" role="status">
                <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-700" aria-hidden="true" />
                <p className="mt-2 text-lg font-bold text-gray-900">{t('proAcademy.detail.geactiveerd', { defaultValue: 'Uw cursus is geactiveerd!' })}</p>
                <p className="mt-1 text-sm text-gray-600">
                  {geactiveerd.pe
                    ? t('proAcademy.detail.peGeregistreerd', { defaultValue: '{{n}} PE-punten zijn geregistreerd. De achteraf-factuur volgt via de opleider.', n: pePunten(geactiveerd.pe) })
                    : t('proAcademy.detail.activatieGelukt', { defaultValue: 'U vindt de details onder "Mijn activaties". De achteraf-factuur volgt via de opleider.' })}
                </p>
                {geactiveerd.metAgenda && (
                  <button
                    type="button"
                    onClick={() => agendaMutation.mutate(geactiveerd.registratieId)}
                    disabled={agendaMutation.isPending}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B1F3A] px-4 py-3 text-sm font-semibold text-white hover:bg-[#1A3459] disabled:opacity-60"
                  >
                    <CalendarPlus className="h-4 w-4" aria-hidden="true" />
                    {t('proAcademy.detail.agenda', { defaultValue: 'Zet alle lesdata in mijn agenda (.ics)' })}
                  </button>
                )}
                {agendaMutation.isError && (
                  <p className="mt-2 text-xs text-red-700" role="alert">
                    {t('proAcademy.detail.agendaFout', { defaultValue: 'Het agenda-bestand kon niet worden gedownload.' })}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <h3 className="text-sm font-semibold text-gray-900">{t('proAcademy.detail.prijsopbouw', { defaultValue: 'Transparante prijsopbouw' })}</h3>
                  <dl className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-500">{t('proAcademy.detail.marktprijs', { defaultValue: 'Normale marktprijs' })}</dt>
                      <dd className="text-gray-600">{prijs(tr.bruto_bedrag)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="inline-flex items-center gap-1 text-emerald-700">
                        <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('proAcademy.detail.collectief', { defaultValue: 'BeLagom Collectief Voordeel ({{pct}}%)', pct: wallet?.korting_pct || 5 })}
                      </dt>
                      <dd className="whitespace-nowrap text-emerald-700">− {prijs(tr.korting_bedrag)}</dd>
                    </div>
                    <div className="flex justify-between gap-3 border-t border-gray-200 pt-2 text-base font-bold text-gray-900">
                      <dt>{t('proAcademy.detail.activeringswaarde', { defaultValue: 'Activeringswaarde uit uw Wallet' })}</dt>
                      <dd className="whitespace-nowrap">{prijs(tr.netto_bedrag)}</dd>
                    </div>
                    {wallet && (
                      <div className="space-y-1 border-t border-dashed border-gray-300 pt-2 text-xs">
                        <div className="flex justify-between text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Wallet className="h-3.5 w-3.5" aria-hidden="true" />
                            {t('proAcademy.detail.budgetResterend', { defaultValue: 'PE-budget {{jaar}} resterend', jaar: wallet.boekjaar })}
                          </span>
                          <span>{eur.format(wallet.budget_resterend)}</span>
                        </div>
                        <div className={`flex justify-between font-medium ${budgetTekort ? 'text-red-700' : 'text-gray-600'}`}>
                          <span>{t('proAcademy.detail.naActivatie', { defaultValue: 'Na activatie' })}</span>
                          <span>{eur.format(Math.max(0, wallet.budget_resterend - tr.netto_bedrag))}</span>
                        </div>
                      </div>
                    )}
                  </dl>
                  <p className="mt-3 text-xs leading-relaxed text-gray-500">
                    {t('proAcademy.detail.factuurUitleg', {
                      defaultValue: 'Het bedrag wordt afgeboekt van uw fictieve PE-budget; de achteraf-factuur gaat naar uw eigen maatschap of opdrachtgever.',
                    })}
                  </p>
                </div>

                {heeftSessies && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">{t('proAcademy.detail.kiesStart', { defaultValue: 'Kies uw startdatum' })}</h3>
                    {sessies.length === 0 && detailQuery.isSuccess ? (
                      <p className="text-sm text-gray-500">{t('proAcademy.detail.geenDatum', { defaultValue: 'Wacht op nieuwe datum' })}</p>
                    ) : (
                      <ul className="space-y-2">
                        {sessies.map((s) => (
                          <SessieSlot key={s.id} sessie={s} gekozen={sessieId === s.id} onKies={() => setSessieId(s.id)} />
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {budgetTekort && (
                  <div className="flex gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    <p>
                      {t('proAcademy.detail.budgetTekortUitleg', {
                        defaultValue: 'Uw PE-budget voor {{jaar}} is ontoereikend voor deze cursus (resterend {{bedrag}}).',
                        jaar: wallet?.boekjaar,
                        bedrag: eur.format(wallet?.budget_resterend ?? 0),
                      })}
                    </p>
                  </div>
                )}

                {activeerMutation.isError && (
                  <p className="text-sm text-red-700" role="alert">
                    {foutMelding(activeerMutation.error, t('proAcademy.detail.activerenMislukt', { defaultValue: 'Activeren is niet gelukt. Probeer het opnieuw.' }))}
                  </p>
                )}

                <button
                  type="button"
                  onClick={() => activeerMutation.mutate()}
                  disabled={!kanActiveren || activeerMutation.isPending}
                  className="w-full rounded-xl px-4 py-3.5 text-sm font-bold text-white shadow-lg transition enabled:hover:brightness-110 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none"
                  style={kanActiveren ? { backgroundColor: '#16a34a', boxShadow: '0 10px 30px -10px rgba(22,163,74,.7)' } : undefined}
                >
                  {activeerMutation.isPending ? '…' : knopTekst}
                </button>
              </>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ProAcademyDetailModal;
